import { EmptyState404Component, EmptyStateUnauthorizedComponent, HeaderComponent } from '@kempower-nx-workspace/chargeye-core';
import { SidePanelComponent } from '@kempower-nx-workspace/ce-side-navigation';
import { CeFeatureFlag, chargEyeTitle, checkFeatureFlagGuard, featureCheckChildGuard } from '@kempower-nx-workspace/chargeye-shared';
import { LICENSE_BASIC, LICENSE_PRO, LicenseGuard, MyKempowerLicenseGuard } from '@kempower-nx-workspace/chargeye-licensing';
import { isAuthenticatedGuard } from '@kempower-nx-workspace/ce-authentication';
import { buildSideNav } from './utils/build-side-nav';
import { RouteExtended } from './types/route-types';
import { TwoColRouteWrapComponent } from '../route-wrap/twoColRouteWrap.component';

const authenticatedTwoColRoutes: RouteExtended[] = [
    /*
     * Redirects – they need to be before normal routes, so redirects work
     */
    {
        path: 'charging-network',
        redirectTo: 'charging-network/overview',
        pathMatch: 'full',
    },
    {
        path: 'charging-network/settings',
        redirectTo: 'charging-network/settings/integrations',
        pathMatch: 'full',
    },
    // 'help' is a non-navigable section; land on its first child like the other sections.
    {
        path: 'help',
        redirectTo: 'help/product-updates',
        pathMatch: 'full',
    },
    // Old routes before grouping pages under 'Charging network'
    {
        path: 'overview',
        redirectTo: 'charging-network/overview',
    },
    {
        path: 'analytics',
        redirectTo: 'charging-network/analytics',
    },
    {
        path: 'integrations',
        redirectTo: 'charging-network/settings/integrations',
    },
    {
        path: 'ev-preconditioning-setup',
        redirectTo: 'vehicles/ev-preconditioning-setup',
    },
    // Old routes before grouping pages under 'Settings'
    {
        path: 'charging-network/integrations',
        redirectTo: 'charging-network/settings/integrations',
    },
    {
        path: 'charging-network/tariffs',
        redirectTo: 'charging-network/settings/tariffs',
    },
    {
        path: 'charging-network/ev-preconditioning-setup',
        redirectTo: 'vehicles/ev-preconditioning-setup',
    },
    // Old route before moving pages under 'Vehicles'.
    {
        path: 'ev-telematics',
        redirectTo: 'vehicles/telematics',
    },
    // Uses a redirectTo function to preserve any deep-link suffix (e.g. /evs/:id/details).
    {
        path: 'charging-network/settings/ev-preconditioning-setup',
        redirectTo: ({ url }) => {
            const rest = url.slice(3).map((s) => s.path).join('/');
            return rest
                ? `/vehicles/ev-preconditioning-setup/${rest}`
                : '/vehicles/ev-preconditioning-setup';
        },
    },

    /*
     * Regular routes
     */
    {
        path: 'charging-network',
        title: 'Charging network',
        data: {
            sideNavTitle: 'Charging network',
            nonNavigableItem: true,
            icon: 'charging-network',
        },
        children: [
            {
                path: 'sites/:siteId',
                title: `Site overview ${chargEyeTitle}`,
                children: [
                    // charging-priority-poc lives here (instead of inside site) only because the
                    // type:feature-page boundary rule forbids site → charging-priority-poc imports.
                    // Once the POC graduates, fold it into site as a child feature
                    // (libs/chargeye/app-pages/site/src/lib/feature/charging-priority-poc/) and
                    // move this route back into siteRoutes.
                    {
                        path: 'charging-priority-poc',
                        title: 'Charging priority POC',
                        data: {
                            requiredPermissions: ['feature.chargingPriority.read'],
                            redirectTo: 'charging-network/overview',
                        },
                        loadChildren: () =>
                            import('@kempower-nx-workspace/charging-priority-poc').then(
                                (mod) => mod.chargingPriorityPocRoutes
                            ),
                    },
                    {
                        path: '',
                        loadChildren: () => import('@kempower-nx-workspace/site').then((mod) => mod.siteRoutes),
                    },
                ],
            },
            // Network view available to all users, but only with a direct URL
            {
                path: 'overview',
                title: `Overview ${chargEyeTitle}`,
                loadChildren: () => import('@kempower-nx-workspace/overview').then((mod) => mod.overviewRoutes),
                data: {
                    sideNavTitle: 'Overview',
                },
            },
            {
                path: 'sessions',
                title: `Sessions ${chargEyeTitle}`,
                loadChildren: () => import('@kempower-nx-workspace/sessions').then((mod) => mod.sessionsRoutes),
                data: {
                    sideNavTitle: 'Sessions',
                },
            },
            {
                path: 'stations',
                title: `Stations${chargEyeTitle}`,
                loadChildren: () => import('@kempower-nx-workspace/stations').then((mod) => mod.stationsRoutes),
                data: {
                    sideNavTitle: 'Stations',
                    featureFlag: CeFeatureFlag.FEATURE_STATIONS_LIST,
                },
            },
            {
                path: 'settings',
                title: 'Settings',
                data: {
                    sideNavTitle: 'Settings',
                    nonNavigableItem: true,
                },
                children: [
                    {
                        path: 'integrations',
                        title: `Integrations ${chargEyeTitle}`,
                        data: {
                            sideNavTitle: 'Integrations',
                        },
                        loadChildren: () => import('@kempower-nx-workspace/integrations').then((mod) => mod.integrationRoutes),
                    },
                    {
                        path: 'tariffs',
                        title: `Tariffs ${chargEyeTitle}`,
                        data: {
                            sideNavTitle: 'Tariffs',
                            featureFlag: CeFeatureFlag.FEATURE_TARIFFS,
                            requiredPermissions: ['fleet.chargingStation.settings.pricing.access'],
                            redirectTo: 'charging-network/overview',
                            secondaryLabel: { type: 'pill', value: 'Preview' },
                        },
                        loadChildren: () => import('@kempower-nx-workspace/tariffs').then((mod) => mod.tariffsRoutes),
                    },
                ]
            },
            {
                path: 'analytics',
                title: `Analytics ${chargEyeTitle}`,
                loadChildren: () =>
                    import('@kempower-nx-workspace/ce-app-analytics').then((mod) => mod.analyticsRoutes),
                data: {
                    sideNavTitle: 'Analytics',
                },
            },
            {
                path: 'sites/:siteId',
                loadChildren: () =>
                    import('@kempower-nx-workspace/site').then(
                        (m) => m.siteRoutes
                    ),
            },
        ],
    },
    {
        path: 'vehicles',
        title: `Vehicles ${chargEyeTitle}`,
        data: {
            sideNavTitle: 'Vehicles',
            icon: 'bus',
            nonNavigableItem: true,
            requiredPermissions: ['ev.access'],
            redirectTo: 'charging-network/overview',
        },
        children: [
            {
                path: '',
                redirectTo: 'registered-vehicles',
                pathMatch: 'full',
            },
            {
                path: 'registered-vehicles',
                loadChildren: () => import('@kempower-nx-workspace/vehicles').then((mod) => mod.vehiclesRoutes),
                title: `Registered vehicles ${chargEyeTitle}`,
                data: {
                    sideNavTitle: 'Registered vehicles',
                },
            },
            {
                path: 'telematics',
                loadChildren: () => import('@kempower-nx-workspace/ev-telematics').then((mod) => mod.evtelematicsRoutes),
                title: `Telematics ${chargEyeTitle}`,
                data: {
                    sideNavTitle: 'Telematics',
                    requiredLicenseLevel: LICENSE_PRO,
                    requiredPermissions: ['ev.telematics.access'],
                    redirectTo: 'charging-network/overview',
                },
            },
            {
                path: 'ev-preconditioning-setup',
                title: `Preconditioning setup ${chargEyeTitle}`,
                loadChildren: () =>
                    import('@kempower-nx-workspace/ev-preconditioning-setup').then(
                        (mod) => mod.evPreconditioningSetupRoutes
                    ),
                data: {
                    sideNavTitle: 'Preconditioning',
                    redirectTo: 'charging-network/overview',
                    requiredLicenseLevel: LICENSE_PRO,
                },
            },
        ],
    },
    {
        path: 'notifications',
        loadChildren: () => import('@kempower-nx-workspace/notifications').then((mod) => mod.notificationsRoutes),
        data: {
            sideNavTitle: 'Notifications',
            icon: 'bell',
            redirectTo: 'charging-network/overview',
            requiredLicenseLevel: LICENSE_BASIC,
        },
    },
    {
        path: 'help',
        title: `Help ${chargEyeTitle}`,
        data: {
            sideNavTitle: 'Help',
            nonNavigableItem: true,
            icon: 'help',
            coreLicenseAllowed: true,
        },
        children: [
            {
                path: 'product-updates',
                title: `Product updates ${chargEyeTitle}`,
                data: {
                    sideNavTitle: 'Product updates',
                    coreLicenseAllowed: true,
                    coreLicenseLanding: true,
                },
                loadChildren: () => import('@kempower-nx-workspace/product-update').then((mod) => mod.helpRoutes),
            },
            {
                path: 'support',
                title: `Support ${chargEyeTitle}`,
                data: {
                    sideNavTitle: 'Support',
                    // Gated by the support-requests flag only. No license-tier restriction and
                    // no requiredPermissions: the core (free) tier must reach this page; the
                    // wizard's submit API enforces fleet.chargingStation.ticketing.write
                    // server-side [REQ-10].
                    featureFlag: CeFeatureFlag.FEATURE_SUPPORT_REQUESTS,
                    coreLicenseAllowed: true,
                },
                loadChildren: () => import('@kempower-nx-workspace/support').then((mod) => mod.supportRoutes),
            },
        ],
    },
    {
        path: 'profile-settings',
        title: 'Profile settings',
        loadChildren: () => import('@kempower-nx-workspace/profile-settings').then((mod) => mod.profileSettingsRoutes),
    },
    {
        path: 'kempower-root',
        title: 'Kempwower root',
        data: {
            sideNavTitle: 'Kempower root',
            nonNavigableItem: true,
            icon: 'settings',
            requiredPermissions: ['admin.organizations.access'], // 1st condition: only System admin can see list of orgs and users
            featureFlag: CeFeatureFlag.FEATURE_ACCESS_MANAGEMENT, // 2nd condition: feature flag enabled
        },
        children: [
            {
                path: 'organizations',
                title: 'Organizations',
                loadChildren: () => import('@kempower-nx-workspace/access-management').then((mod) => mod.accessManagementRoutes),
                data: {
                    sideNavTitle: 'Organizations',
                },
            },
        ]
    },
    {
        path: 'organization-settings',
        title: 'Organization settings',
        loadChildren: () =>
            import('@kempower-nx-workspace/access-management').then((mod) => mod.organizationSettingsRoutes),
    },
    {
        path: 'access-management',
        title: 'Access management',
        children: [

            {
                path: 'users',
                title: 'Users',
                loadChildren: () => import('@kempower-nx-workspace/access-management').then((mod) => mod.usersRoutes),
            },
            {
                path: 'roles',
                title: 'Roles',
                loadChildren: () => import('@kempower-nx-workspace/access-management').then((mod) => mod.orgRolesRoutes),
            },
            {
                path: 'resource-groups',
                title: 'Resource groups',
                loadChildren: () => import('@kempower-nx-workspace/access-management').then((mod) => mod.resourceGroupsRoutes),
            },
            {
                path: 'sent-invites',
                title: 'Sent invites',
                loadChildren: () => import('@kempower-nx-workspace/access-management').then((mod) => mod.sentInvitesRoutes),
            },
            {
                path: 'received-invites',
                title: 'Received invites',
                loadChildren: () => import('@kempower-nx-workspace/access-management').then((mod) => mod.receivedInvitesRoutes),
            },
        ],
    },

    /*
     * 404
     */
    {
        path: '404',
        component: EmptyState404Component,
    },
    {
        path: '**',
        redirectTo: '404',
        pathMatch: 'full',
    },
];

export const chargeyeShellRoute: RouteExtended[] = [
    {
        path: '',
        redirectTo: 'charging-network/overview',
        pathMatch: 'full',
    },
    {
        path: 'unauthorized',
        component: EmptyStateUnauthorizedComponent,
    },
    {
        path: 'auth',
        title: 'Authentication',
        data: {
            authSiteName: 'ChargEye',
        },
        loadChildren: () => import('@kempower-nx-workspace/auth0-authentications').then((mod) => mod.auth0Routes),
    },

    {
        path: '',
        component: TwoColRouteWrapComponent,
        canActivate: [isAuthenticatedGuard, MyKempowerLicenseGuard, LicenseGuard, checkFeatureFlagGuard],
        canActivateChild: [featureCheckChildGuard],
        runGuardsAndResolvers: 'always',
        children: [
            {
                path: 'charging-session/:id',
                outlet: 'dialog',
                loadComponent: () =>
                    import('@kempower-nx-workspace/charging-session-v2').then((m) => m.ChargingSessionComponent),
            },
            {
                path: 'charging-scheduled-session/:id',
                outlet: 'dialog',
                loadComponent: () =>
                    import('@kempower-nx-workspace/charging-session-v2').then((m) => m.ChargingSessionComponent),
            },
            {
                path: '',
                children: [
                    { path: '', component: HeaderComponent, outlet: 'header' },
                    {
                        path: '',
                        component: SidePanelComponent,
                        data: { routeMenu: buildSideNav(authenticatedTwoColRoutes) },
                        outlet: 'sidePanel',
                    },
                    ...authenticatedTwoColRoutes,
                ],
            },
        ],
    },
];
